import React, { useEffect, useState } from 'react';
import { Box, Card, Typography, Breadcrumbs, Divider, Button, Skeleton } from '@mui/material';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Formik, Form } from 'formik';
import { useDispatch } from 'react-redux';
import { useResponseHandler } from 'hooks/useResponseHandler';
import validationSchema from './formik/validationSchema';
import planesFormModel from './formik/planesFormModel';
import { PlanesForm } from './components/forms/PlanesForm';
import {
  editServicesIaPlanStore,
  getServicesIaPlanByIdStore,
} from 'store/services-ia-plans/thunks';
import { MessagesDrawerRight } from 'components/MessagesDrawerRight';
import formInitialValues from './formik/formInitialValues';
import { servicesIaPlansMapper } from './mappers/services-ia-plans.mapper';
import { currencies } from 'commons/constants/currencies';

export const EditPlanes = () => {
  const dispatch: any = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const { handleResponse, ResponseComponents } = useResponseHandler();
  const { formId, formField } = planesFormModel;
  const [dataErrors, setMsgErrors] = useState([]);

  const [initialValues, setInitialValues] = useState<any>(formInitialValues);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      if (!id) return;
      setLoading(true);

      const response = await dispatch(getServicesIaPlanByIdStore(id));
      if (response?.ok && response?.data) {
        const item = response.data;
        const mappedDomain = servicesIaPlansMapper.toDomain(item);

        setInitialValues({
          name: mappedDomain.name || '',
          description: mappedDomain.description || '',
          monthlyPrice: mappedDomain.monthlyPrice ?? '',
          currency:
            currencies.find((c: any) => c.value === (mappedDomain.currency || 'USD')) || null,
          monthlyCredits: mappedDomain.monthlyCredits ?? '',
          durationDays: mappedDomain.durationDays ?? '',
          maxRequestsDay: mappedDomain.maxRequestsDay ?? '',
          maxRequestsMonth: mappedDomain.maxRequestsMonth ?? '',
          maxAssistants: mappedDomain.maxAssistants ?? '',
          maxAgents: mappedDomain.maxAgents ?? '',
          maxUsers: mappedDomain.maxUsers ?? '',
          allowChat: mappedDomain.allowChat ?? false,
          allowImages: mappedDomain.allowImages ?? false,
          allowAudio: mappedDomain.allowAudio ?? false,
          allowDocuments: mappedDomain.allowDocuments ?? false,
          allowEmbeddings: mappedDomain.allowEmbeddings ?? false,
          allowMemory: mappedDomain.allowMemory ?? false,
          allowTools: mappedDomain.allowTools ?? false,
          allowRag: mappedDomain.allowRag ?? false,
          sortOrder: mappedDomain.sortOrder ?? '',
        });
      } else {
        navigate('/manager/servicios-ia/comercial/planes');
      }
      setLoading(false);
    };

    loadData();
  }, [id, dispatch, navigate]);

  const onSubmit = async (values: any, actions: any) => {
    if (!id) return;
    const payload = servicesIaPlansMapper.toDto(values);
    const response = await dispatch(editServicesIaPlanStore(id, payload));

    await handleResponse(response, 'Plan Actualizado Exitosamente', true);
    actions.setSubmitting(false);

    if (!response?.ok && response?.errors) {
      setMsgErrors(response.errors);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 4 }}>
        <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 3 }} />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', mb: 5 }}>
      {ResponseComponents}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1, color: 'text.primary' }}>
            Editar Plan
          </Typography>
          <Breadcrumbs aria-label="breadcrumb">
            <Link color="inherit" to="/manager/plataforma-ia/comercial/planes">
              Planes IA
            </Link>
            <Typography color="text.primary">Editar Plan</Typography>
          </Breadcrumbs>
        </Box>
      </Box>

      <Card sx={{ p: 4, borderRadius: 3, boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.05)' }}>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={onSubmit}
          enableReinitialize
        >
          {(props) => (
            <Form id={formId}>
              <PlanesForm formField={formField} isEditing={true} />

              <Divider sx={{ my: 4 }} />

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button
                  component={Link}
                  to="/manager/servicios-ia/comercial/planes"
                  variant="outlined"
                  color="inherit"
                  sx={{ borderRadius: 2, px: 3 }}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={props.isSubmitting}
                  sx={{
                    borderRadius: 2,
                    px: 4,
                    boxShadow: '0px 4px 10px rgba(105, 108, 255, 0.3)',
                  }}
                >
                  {props.isSubmitting ? 'Guardando...' : 'Actualizar Plan'}
                </Button>
              </Box>
            </Form>
          )}
        </Formik>
      </Card>

      <MessagesDrawerRight data={dataErrors} open={dataErrors.length > 0} setMsg={setMsgErrors} />
    </Box>
  );
};
